import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './tokenHistory.css'
import { getRechargeHistory, RechargeRecord } from '../functions'
import { getToken, getUsername } from '../utils/auth'

interface Transaction {
    id: string
    type: 'consumption' | 'recharge'
    description: string
    timestamp: string
    amount: number
    balance?: number
    status?: 'successful' | 'failed' | 'pending'
}

const TokenHistory = () => {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState<'consumption' | 'recharge'>('consumption')
    const [rechargeRecords, setRechargeRecords] = useState<Transaction[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleClose = () => {
        navigate(-1)
    }

    // 获取充值记录
    useEffect(() => {
        const fetchRechargeHistory = async () => {
            if (activeTab !== 'recharge') return

            const token = getToken()
            const uid = getUsername()

            if (!token || !uid) {
                setError('请先登录')
                return
            }

            setIsLoading(true)
            setError(null)

            try {
                const result = await getRechargeHistory(token, uid)
                if (result.success) {
                    const records: Transaction[] = result.data.records.map((record: RechargeRecord) => ({
                        id: record.id,
                        type: 'recharge' as const,
                        description: getRecordTypeDescription(record.type),
                        timestamp: formatTimestamp(record.timestamp),
                        amount: record.dcoinAmount,
                        status: record.status
                    }))
                    setRechargeRecords(records)
                } else {
                    setError(result.message || '获取充值记录失败')
                }
            } catch (err) {
                console.error('获取充值记录错误:', err)
                setError('获取充值记录失败')
            } finally {
                setIsLoading(false)
            }
        }

        fetchRechargeHistory()
    }, [activeTab])

    // 将记录类型转换为描述
    const getRecordTypeDescription = (type: string): string => {
        switch (type) {
            case 'purchase':
                return '代币充值'
            case 'daily_reward':
                return '每日奖励'
            case 'refund':
                return '退款'
            default:
                return '其他'
        }
    }

    // 格式化时间戳
    const formatTimestamp = (timestamp: string): string => {
        try {
            const date = new Date(timestamp)
            return date.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            }).replace(/\//g, '/')
        } catch {
            return timestamp
        }
    }

    // 获取状态标签
    const getStatusLabel = (status?: string): string => {
        switch (status) {
            case 'successful':
                return '成功'
            case 'failed':
                return '失败'
            case 'pending':
                return '处理中'
            default:
                return ''
        }
    }

    // 获取状态样式类
    const getStatusClass = (status?: string): string => {
        switch (status) {
            case 'successful':
                return 'status-success'
            case 'failed':
                return 'status-failed'
            case 'pending':
                return 'status-pending'
            default:
                return ''
        }
    }

    // 消费记录（暂无API，显示空）
    // TODO: 接入消费记录 API 后替换此数组
    const consumptionTransactions: Transaction[] = []

    // 根据当前标签获取交易记录
    const getFilteredTransactions = (): Transaction[] => {
        if (activeTab === 'consumption') {
            return consumptionTransactions
        }
        return rechargeRecords
    }

    const filteredTransactions = getFilteredTransactions()

    return (
        <div className="token-history-page">
            {/* Close Button */}
            <button className="token-history-close-btn" onClick={handleClose} aria-label="关闭">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>

            {/* Main Content */}
            <div className="token-history-content">
                {/* Title */}
                <h1 className="token-history-title">代币历史</h1>

                {/* Tabs */}
                <div className="token-history-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'consumption' ? 'active' : ''}`}
                        onClick={() => setActiveTab('consumption')}
                    >
                        消费记录
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'recharge' ? 'active' : ''}`}
                        onClick={() => setActiveTab('recharge')}
                    >
                        充值记录
                    </button>
                </div>

                {/* Loading State */}
                {isLoading && activeTab === 'recharge' && (
                    <div className="loading-state">
                        <p>加载中...</p>
                    </div>
                )}

                {/* Error State */}
                {error && activeTab === 'recharge' && (
                    <div className="error-state">
                        <p>{error}</p>
                    </div>
                )}

                {/* Transaction List */}
                {!isLoading && !error && (
                    <div className="transaction-list">
                        {filteredTransactions.length === 0 ? (
                            <div className="empty-state">
                                <p>暂无{activeTab === 'consumption' ? '消费' : '充值'}记录</p>
                            </div>
                        ) : (
                            filteredTransactions.map((transaction) => (
                                <div key={transaction.id} className="transaction-item">
                                    <div className="transaction-left">
                                        <div className={`transaction-tag ${transaction.type === 'consumption' ? 'consumption' : 'recharge'}`}>
                                            {transaction.type === 'consumption' ? '消费' : '充值'}
                                        </div>
                                        <div className="transaction-details">
                                            <p className="transaction-description">{transaction.description}</p>
                                            <p className="transaction-timestamp">{transaction.timestamp}</p>
                                            {transaction.status && (
                                                <span className={`transaction-status ${getStatusClass(transaction.status)}`}>
                                                    {getStatusLabel(transaction.status)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="transaction-right">
                                        <p className={`transaction-amount ${transaction.type === 'consumption' ? 'consumption' : 'recharge'}`}>
                                            {transaction.type === 'consumption' ? '-' : '+'}{transaction.amount}
                                        </p>
                                        {transaction.balance !== undefined && (
                                            <p className="transaction-balance">余额:{transaction.balance} DCOIN</p>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default TokenHistory
